import React, { useCallback } from 'react';
import { useIsSafari } from '~/context/common/DeviceContext';
import useGetElementOffsetTop from './useGetElementOffsetTop';

/*
 * This is needed because Safari does not
 * smoothly scroll to anchor tag sections
 * */

type OwnProps = React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
> &
  React.PropsWithChildren<{
    beforeUpdate?: () => void;
    href: string;
  }>;

const SmoothAnchor = ({
  children,
  'data-analytics-id': dataAnalyticsId,
  'data-analytics-attributes': dataAnalyticsAttributes = '',
  beforeUpdate = () => {},
  href,
  ...props
}: OwnProps) => {
  const getElementOffsetTop = useGetElementOffsetTop();
  const isSafari = useIsSafari();
  const idWithoutHash = href.slice(1);

  const onClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    e => {
      beforeUpdate();
      if (!isSafari) return;
      const section = document.getElementById(idWithoutHash);
      if (!section) return;
      e.preventDefault();
      const topOffset = getElementOffsetTop(section);

      window.scroll({
        top: topOffset,
        behavior: 'smooth'
      });
    },
    [beforeUpdate, getElementOffsetTop, idWithoutHash, isSafari]
  );

  return (
    <a
      data-analytics-id={dataAnalyticsId}
      data-analytics-attributes={dataAnalyticsAttributes}
      href={href}
      onClick={onClick}
      {...props}
    >
      {children}
    </a>
  );
};

export default SmoothAnchor;
